/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { FieldButton, FieldInput, Form, InputAdornment } from "@adapptio/lib-editor-ui";
import { COLOR } from "@adapptio/lib-editor-ui/src/enums/COLOR";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { useDeepCompareMemo } from "use-deep-compare";
import { TDialogComponent } from "../../../dialogs/Dialog";
import { normalizeResourcePath } from "../../../resources/ResourceHelpers";
import { ACTION_BUTTON_SIZE, ACTION_BUTTON_STYLE, ActionButton } from "../../../ui/components/ActionButton";
import { DialogButtons } from "../../../ui/components/Dialogs/DialogButtons";
import {
	DIALOG_CONTENT_ALIGN,
	DIALOG_CONTENT_FLOW,
	DialogContent
} from "../../../ui/components/Dialogs/DialogContent";
import { DialogHeader } from "../../../ui/components/Dialogs/DialogHeader";
import { DIALOG_SIZE, DialogPanel } from "../../../ui/components/Dialogs/DialogPanel";
import { FieldGroup } from "../../../ui/components/Fields/FieldGroup";
import { FieldRow } from "../../../ui/components/Fields/FieldRow";
import { IResourceListProps, IResourceNode, ResourceList } from "../../../ui/components/ResourceList";
import { useEditorMainContext } from "../../../ui/contexts/EditorMainContext";
import { UploadAssetDialog } from "./UploadAssetDialog";
import { useStateWithEvent } from "../../../ui/hooks";
import { isDefined } from "@adapptio/lib-shared";

const allowedResourceTypes: RESOURCE_TYPES[] = [ RESOURCE_TYPES.DIRECTORY ];

export const UploadAssetDialogComponent: TDialogComponent<UploadAssetDialog> = ({ dialog }) => {
	const baseClass = BEM("edt-module-upload-asset-dialog");

	const { appInfo } = useEditorMainContext();

	const fileInputRef = useRef(null);
	const [ files, setFiles ] = useState<FileList | null>(null);

	const pending = useStateWithEvent(dialog.onPendingChange, () => dialog.isPending());

	const params = useMemo(() => dialog.getParams(), []);
	const hideDirSelector = isDefined(params.basePath);

	const [ selectedParentNode, setSelectedParentNode ] = useState<IResourceNode>(
		dialog.getParentResourceNode()
	);

	const selectResource = useCallback<IResourceListProps["onSelect"]>((selectedNode) => {
		setSelectedParentNode(selectedNode);
	}, []);

	const basePath = useDeepCompareMemo(() => {
		if (isDefined(params.basePath)) {
			return params.basePath;
		}

		if (selectedParentNode && !selectedParentNode?.isRoot) {
			return normalizeResourcePath(selectedParentNode.basePath, selectedParentNode.name);
		}
		return "";
	}, [ selectedParentNode ]);

	const handleClose = useCallback(() => {
		dialog.close({
			resourceId: null
		});
	}, [ dialog ]);

	const submit = useCallback(() => {
		dialog.upload(basePath, files);
	}, [ dialog, basePath, files ]);

	const openFileUpload = useCallback(() => {
		fileInputRef.current.click();
	}, []);

	const handleFileChange = useCallback<React.ChangeEventHandler<HTMLInputElement>>((event) => {
		const files = event.target.files;
		setFiles(files);
	}, []);

	return (
		<DialogPanel size={DIALOG_SIZE.SMALL} className={baseClass.toClassName()}>
			<Form onSubmit={submit} noValidate>
				<DialogHeader
					title={dialog.isUpdate() ? "Update asset" : "Add asset"}
					icon="mdi/file-upload"
					onClose={handleClose}
				/>
				<DialogContent flow={DIALOG_CONTENT_FLOW.COLUMN} align={DIALOG_CONTENT_ALIGN.STRETCH}>
					<FieldRow>
						<FieldGroup>
							<FieldInput
								value=""
								type="file"
								multiple={!dialog.isUpdate()}
								inputRef={fileInputRef}
								onChange={handleFileChange}
								hidden
								startAdornment={
									<InputAdornment>
										<FieldButton color={COLOR.PRIMARY} onClick={openFileUpload}>
											Choose File
										</FieldButton>
									</InputAdornment>
								}
								endAdornment={
									<InputAdornment>
										<label>
											{files
												? Array.from(files)
														.map((file) => file.name)
														.join(", ")
												: ""}
										</label>
									</InputAdornment>
								}
							/>
						</FieldGroup>
					</FieldRow>

					{!hideDirSelector && (
						<label className={baseClass.element("field-row-resource-list").toClassName()}>
							<ResourceList
								onSelect={selectResource}
								labelSearch="Select target directory"
								allowedResourceTypes={allowedResourceTypes}
								selectedId={selectedParentNode?.id ?? "__root__"}
							/>
						</label>
					)}
				</DialogContent>
				<DialogButtons>
					<ActionButton
						label="Cancel"
						icon="mdi/close"
						style={ACTION_BUTTON_STYLE.DEFAULT}
						size={ACTION_BUTTON_SIZE.LARGE}
						onClick={handleClose}
						disabled={pending}
					/>
					<ActionButton
						label={dialog.isUpdate() ? "Update" : "Upload"}
						icon="mdi/check"
						style={ACTION_BUTTON_STYLE.PRIMARY}
						size={ACTION_BUTTON_SIZE.LARGE}
						onClick={submit}
						disabled={pending || !files}
						isLoading={pending}
					/>
				</DialogButtons>
			</Form>
		</DialogPanel>
	);
};
